import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Send, RefreshCw, Inbox as InboxIcon, SendHorizontal, Reply, Trash2, ExternalLink, Save, AlertCircle, Eye, UserPlus, MessageSquare, AtSign, Archive, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import SafeHtml from '../components/SafeHtml';
import RichTextEditor from '../components/RichTextEditor';
import EmailChipInput from '../components/EmailChipInput';
import CommentInput from '../components/CommentInput';
import EmailBodySandbox from '../components/EmailBodySandbox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const EMAILS_PER_PAGE = 20;

const LoadMoreSentinel = ({ onLoadMore, hasMore, loading }) => {
    const sentinelRef = useRef(null);
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        if (!hasMore || loading) return;
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) onLoadMoreRef.current();
            },
            { rootMargin: '100px', threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loading]);

    if (!hasMore) return null;
    return (
        <div ref={sentinelRef} className="flex justify-center py-3 min-h-[40px]">
            {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
        </div>
    );
};

// Color palette for account dots
const ACCOUNT_COLORS = [
    'bg-blue-500',
    'bg-orange-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-cyan-500',
    'bg-red-500',
];

// Epic 18.1: Collapsible comment section - Comment button expands to show input
const CommentButtonWithInput = ({ teamMembers, onSave }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="mt-3">
            {!expanded ? (
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                    onClick={() => setExpanded(true)}
                >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Add internal note
                </Button>
            ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-2">
                    <CommentInput
                        teamMembers={teamMembers}
                        onSave={(data) => {
                            onSave(data);
                            setExpanded(false);
                        }}
                        placeholder="Add internal note... Type @ to mention"
                    />
                    <button
                        type="button"
                        className="mt-1 text-xs text-slate-500 hover:text-slate-700"
                        onClick={() => setExpanded(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

// Generate consistent color for an account based on its ID
const getAccountColor = (accountId, accounts) => {
    const index = accounts.findIndex(acc => acc.id === accountId);
    // Handle case where account is not found (return first color as fallback)
    if (index === -1) {
        return ACCOUNT_COLORS[0];
    }
    return ACCOUNT_COLORS[index % ACCOUNT_COLORS.length];
};

const InboxPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [emails, setEmails] = useState([]);
    const [threads, setThreads] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);
    const [threadEmails, setThreadEmails] = useState([]);
    const [syncing, setSyncing] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [activeFolder, setActiveFolder] = useState('inbox');
    const [selectedMailbox, setSelectedMailbox] = useState('all'); // 'all' or account_id
    const [viewMode, setViewMode] = useState('threads'); // 'threads' or 'emails'
    const [filterMode, setFilterMode] = useState('all'); // Epic 18.2: 'all' or 'mentioned'
    const [folderCounts, setFolderCounts] = useState({ inbox: 0, sent: 0, discard: 0, junk: 0 });
    const [totalThreads, setTotalThreads] = useState(0);
    const [totalEmails, setTotalEmails] = useState(0);
    const [hasMoreThreads, setHasMoreThreads] = useState(false);
    const [hasMoreEmails, setHasMoreEmails] = useState(false);
    const [loadingMoreThreads, setLoadingMoreThreads] = useState(false);
    const [loadingMoreEmails, setLoadingMoreEmails] = useState(false);
    const [selectedThreadIds, setSelectedThreadIds] = useState(new Set());
    const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());
    const [teamMembers, setTeamMembers] = useState([]);
    const [comments, setComments] = useState([]); // Epic 18.1: comments for selected thread
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [showReplyCcBcc, setShowReplyCcBcc] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [creatingLeadId, setCreatingLeadId] = useState(null);
    const [sendData, setSendData] = useState({
        account_id: '',
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        subject: '',
        body: ''
    });
    const [replyData, setReplyData] = useState({
        account_id: '',
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        subject: '',
        body: '',
        in_reply_to: null,
        references: null,
        reply_all: false
    });

    // Helper function to parse comma-separated emails
    const parseEmailList = (value) => {
        return value ? value.split(',').map(email => email.trim()).filter(email => email) : [];
    };

    useEffect(() => {
        fetchAccounts();
        fetchTeamMembers();
    }, []);

    useEffect(() => {
        if (viewMode === 'threads') {
            fetchThreads();
        } else {
            fetchEmails();
        }
        fetchFolderCounts();
    }, [activeFolder, selectedMailbox, viewMode, filterMode]);

    useEffect(() => {
        setSelectedThreadIds(new Set());
        setSelectedEmailIds(new Set());
    }, [activeFolder, selectedMailbox, viewMode, filterMode]);

    // Debug: Log current state
    useEffect(() => {
        console.log('InboxPage Debug:', {
            activeFolder,
            viewMode,
            selectedEmail: selectedEmail?.id,
            emailsCount: emails.length,
            threadsCount: threads.length
        });
    }, [activeFolder, viewMode, selectedEmail, emails.length, threads.length]);

    // Memoized account map for O(1) lookups
    const accountMap = useMemo(() => {
        return new Map(accounts.map(acc => [acc.id, acc]));
    }, [accounts]);

    const inboundThreadEmail = useMemo(() => {
        return threadEmails.find((email) => email.direction === 'inbound') || null;
    }, [threadEmails]);

    const fetchThreads = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/inbox/threads?folder=${activeFolder}&limit=${EMAILS_PER_PAGE}&offset=0`;
            if (selectedMailbox !== 'all') {
                url += `&account_id=${selectedMailbox}`;
            }
            if (filterMode === 'mentioned') {
                url += '&filter_mode=mentioned';
            }
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = response.data;
            setThreads(data.threads || []);
            setTotalThreads(data.total ?? 0);
            setHasMoreThreads(data.has_more ?? false);
        } catch (error) {
            toast.error('Failed to fetch threads');
        }
    };

    const fetchMoreThreads = useCallback(async () => {
        if (!hasMoreThreads || loadingMoreThreads) return;
        setLoadingMoreThreads(true);
        try {
            const token = localStorage.getItem('token');
            const offset = threads.length;
            let url = `${API_URL}/inbox/threads?folder=${activeFolder}&limit=${EMAILS_PER_PAGE}&offset=${offset}`;
            if (selectedMailbox !== 'all') url += `&account_id=${selectedMailbox}`;
            if (filterMode === 'mentioned') url += '&filter_mode=mentioned';
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = response.data;
            setThreads(prev => [...prev, ...(data.threads || [])]);
            setTotalThreads(data.total ?? totalThreads);
            setHasMoreThreads(data.has_more ?? false);
        } catch (error) {
            toast.error('Failed to load more threads');
        } finally {
            setLoadingMoreThreads(false);
        }
    }, [activeFolder, selectedMailbox, filterMode, threads.length, hasMoreThreads, loadingMoreThreads, totalThreads]);

    const fetchTeamMembers = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_URL}/team/members`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeamMembers(data);
        } catch (error) {
            console.error('Failed to fetch team members:', error);
        }
    };

    const fetchComments = async (threadId) => {
        if (!threadId) {
            setComments([]);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_URL}/comments?thread_id=${threadId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setComments(data);
        } catch (error) {
            console.error('Failed to fetch comments:', error);
            setComments([]);
        }
    };

    const fetchEmails = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/emails?folder=${activeFolder}&limit=${EMAILS_PER_PAGE}&offset=0`;
            if (selectedMailbox !== 'all') {
                url += `&account_id=${selectedMailbox}`;
            }
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = response.data;
            setEmails(data.emails || []);
            setTotalEmails(data.total ?? 0);
            setHasMoreEmails(data.has_more ?? false);
        } catch (error) {
            toast.error('Failed to fetch emails');
        }
    };

    const fetchMoreEmails = useCallback(async () => {
        if (!hasMoreEmails || loadingMoreEmails) return;
        setLoadingMoreEmails(true);
        try {
            const token = localStorage.getItem('token');
            const offset = emails.length;
            let url = `${API_URL}/emails?folder=${activeFolder}&limit=${EMAILS_PER_PAGE}&offset=${offset}`;
            if (selectedMailbox !== 'all') url += `&account_id=${selectedMailbox}`;
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = response.data;
            setEmails(prev => [...prev, ...(data.emails || [])]);
            setTotalEmails(data.total ?? totalEmails);
            setHasMoreEmails(data.has_more ?? false);
        } catch (error) {
            toast.error('Failed to load more emails');
        } finally {
            setLoadingMoreEmails(false);
        }
    }, [activeFolder, selectedMailbox, emails.length, hasMoreEmails, loadingMoreEmails, totalEmails]);

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(`${API_URL}/accounts`);
            setAccounts(response.data);
        } catch (error) {
            toast.error('Failed to fetch accounts');
        }
    };

    const fetchFolderCounts = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/emails/folder-counts`;
            if (selectedMailbox !== 'all') {
                url += `?account_id=${selectedMailbox}`;
            }
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setFolderCounts(response.data);
        } catch (error) {
            console.error('Failed to fetch folder counts:', error);
        }
    };

    const moveToDiscard = async (emailId) => {
        try {
            await axios.post(`${API_URL}/emails/${emailId}/move-to-discard`);
            toast.success('Email moved to discard');

            // If we're viewing a thread and deleted an email from it, refresh the thread
            if (viewMode === 'threads' && selectedThread) {
                // Remove the deleted email from threadEmails immediately
                setThreadEmails(prev => {
                    const updated = prev.filter(e => e.id !== emailId);
                    // If thread is now empty, clear the selection
                    if (updated.length === 0) {
                        setSelectedThread(null);
                    }
                    return updated;
                });
                // Also refresh threads list to update counts
                fetchThreads();
            } else if (viewMode === 'threads') {
                fetchThreads();
            } else {
                // For emails view, remove from list and clear selection if it was selected
                setEmails(prev => prev.filter(e => e.id !== emailId));
                if (selectedEmail?.id === emailId) {
                    setSelectedEmail(null);
                }
            }
            fetchFolderCounts();
        } catch (error) {
            toast.error('Failed to move email to discard');
        }
    };

    const toggleThreadSelection = (threadId, e) => {
        e?.stopPropagation();
        setSelectedThreadIds(prev => {
            const next = new Set(prev);
            if (next.has(threadId)) next.delete(threadId);
            else next.add(threadId);
            return next;
        });
    };

    const toggleEmailSelection = (emailId, e) => {
        e?.stopPropagation();
        setSelectedEmailIds(prev => {
            const next = new Set(prev);
            if (next.has(emailId)) next.delete(emailId);
            else next.add(emailId);
            return next;
        });
    };

    const selectAllThreads = () => {
        if (selectedThreadIds.size === threads.length) {
            setSelectedThreadIds(new Set());
        } else {
            setSelectedThreadIds(new Set(threads.map(t => t.thread_id)));
        }
    };

    const selectAllEmails = () => {
        if (selectedEmailIds.size === emails.length) {
            setSelectedEmailIds(new Set());
        } else {
            setSelectedEmailIds(new Set(emails.map(e => e.id)));
        }
    };

    const bulkMoveToDiscard = async () => {
        const token = localStorage.getItem('token');
        try {
            if (viewMode === 'threads' && selectedThreadIds.size > 0) {
                const ids = Array.from(selectedThreadIds);
                const { data } = await axios.post(
                    `${API_URL}/emails/bulk-move-threads-to-discard`,
                    { thread_ids: ids },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const moved = data.moved_count ?? ids.length;
                toast.success(data.message || `Moved ${moved} email(s) to discard`);
                setThreads(prev => prev.filter(t => !ids.includes(t.thread_id)));
                setTotalThreads(prev => Math.max(0, prev - moved));
                setSelectedThreadIds(new Set());
                if (selectedThread && ids.includes(selectedThread.thread_id)) {
                    setSelectedThread(null);
                    setThreadEmails([]);
                }
            } else if (viewMode === 'emails' && selectedEmailIds.size > 0) {
                const ids = Array.from(selectedEmailIds);
                const { data } = await axios.post(
                    `${API_URL}/emails/bulk-move-to-discard`,
                    { email_ids: ids },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const moved = data.moved_count ?? ids.length;
                toast.success(data.message || `Moved ${moved} email(s) to discard`);
                setEmails(prev => prev.filter(e => !ids.includes(e.id)));
                setTotalEmails(prev => Math.max(0, prev - moved));
                setSelectedEmailIds(new Set());
                if (selectedEmail && ids.includes(selectedEmail.id)) {
                    setSelectedEmail(null);
                }
            }
            fetchFolderCounts();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to move to discard'));
        }
    };

    const clearSelection = () => {
        setSelectedThreadIds(new Set());
        setSelectedEmailIds(new Set());
    };

    const syncEmails = async () => {
        setSyncing(true);
        try {
            await axios.post(`${API_URL}/emails/sync`);
            toast.success('Email sync triggered');
            setTimeout(() => {
                if (viewMode === 'threads') {
                    fetchThreads();
                    // Refresh the selected thread's emails if one is selected
                    if (selectedThread) {
                        selectThread(selectedThread);
                    }
                } else {
                    fetchEmails();
                }
                fetchFolderCounts();
            }, 2000);
        } catch (error) {
            toast.error('Failed to sync emails');
        } finally {
            setSyncing(false);
        }
    };

    const selectThread = async (thread) => {
        setSelectedThread(thread);
        setSelectedEmail(null);
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${API_URL}/emails/by-thread/${thread.thread_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const sortedEmails = response.data.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return dateA - dateB;
            });
            setThreadEmails(sortedEmails);
            fetchComments(thread.thread_id);
            // Epic 18.2: Mark mention notifications as read when user views thread
            await axios.put(`${API_URL}/notifications/mark-thread-read?thread_id=${thread.thread_id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            }).catch(() => { });
        } catch (error) {
            toast.error('Failed to fetch thread emails');
        }
    };

    // Open thread when navigating from notification click (state.openThreadId)
    useEffect(() => {
        const openThreadId = location.state?.openThreadId;
        if (openThreadId) {
            setActiveFolder('inbox');
            setViewMode('threads');
            setFilterMode('mentioned');
            selectThread({ thread_id: openThreadId });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.openThreadId]);

    // Handle account selection and append signature
    const handleAccountSelect = (accountId, isReply = false) => {
        const account = accounts.find(acc => acc.id === accountId);
        if (account && account.signature_html) {
            const bodyWithSignature = `<br/><br/>${account.signature_html}`;
            if (isReply) {
                setReplyData({ ...replyData, account_id: accountId, body: bodyWithSignature });
            } else {
                setSendData({ ...sendData, account_id: accountId, body: bodyWithSignature });
            }
        } else {
            if (isReply) {
                setReplyData({ ...replyData, account_id: accountId });
            } else {
                setSendData({ ...sendData, account_id: accountId });
            }
        }
    };

    // Start reply to an email
    const startReply = (email, replyAll = false) => {
        const subject = email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`;

        // Get my email addresses
        const myEmails = accounts.map(acc => acc.email.toLowerCase());

        // Prepare To field
        let toEmails = [email.from_email];

        // Prepare CC field for Reply All
        let ccEmails = [];
        if (replyAll) {
            // Add original To recipients (excluding self)
            const originalTo = email.to_email ? [email.to_email] : [];
            ccEmails = [...originalTo, ...(email.cc_emails || [])]
                .filter(e => e && !myEmails.includes(e.toLowerCase()))
                .filter((e, i, arr) => arr.indexOf(e) === i); // unique
        }

        // Use the account that received this email, or default to first account
        const replyAccount = accounts.find(acc => acc.id === email.account_id) || accounts[0];
        const bodyWithSignature = replyAccount?.signature_html ? `<br/><br/>${replyAccount.signature_html}` : '';

        setReplyData({
            account_id: replyAccount?.id || '',
            to_emails: toEmails,
            cc_emails: ccEmails,
            bcc_emails: [],
            subject: subject,
            body: bodyWithSignature,
            in_reply_to: email.message_id,
            references: email.references ? `${email.references} ${email.message_id}` : email.message_id || null,
            reply_all: replyAll
        });

        setShowReplyCcBcc(replyAll && ccEmails.length > 0);
        setReplyDialogOpen(true);
    };

    const validateVariables = async (body, subject, leadId = null) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/emails/validate-variables`,
                {
                    body,
                    subject,
                    lead_id: leadId
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const missingFields = response.data.missing_fields || [];
            const valid = response.data.valid || false;

            // Update state for UI consistency
            setMissingFields(missingFields);

            return { valid, missingFields };
        } catch (error) {
            console.error('Failed to validate variables:', error);
            // Allow sending if validation fails, but clear missing fields
            setMissingFields([]);
            return { valid: true, missingFields: [] };
        }
    };

    const createLeadFromEmail = async (email) => {
        if (!email?.id) return;
        setCreatingLeadId(email.id);
        try {
            const response = await axios.post(`${API_URL}/emails/${email.id}/create-lead`);
            const leadId = response.data?.lead_id;

            if (leadId) {
                const threadId = email.thread_id;
                setEmails((prev) => prev.map((item) => (
                    (threadId && item.thread_id === threadId) || item.id === email.id
                        ? { ...item, lead_id: leadId }
                        : item
                )));
                setThreads((prev) => prev.map((thread) => (
                    threadId && thread.thread_id === threadId
                        ? { ...thread, lead_id: leadId }
                        : thread
                )));
                setThreadEmails((prev) => prev.map((item) => (
                    (threadId && item.thread_id === threadId) || item.id === email.id
                        ? { ...item, lead_id: leadId }
                        : item
                )));
                if (selectedEmail && (selectedEmail.id === email.id || (threadId && selectedEmail.thread_id === threadId))) {
                    setSelectedEmail({ ...selectedEmail, lead_id: leadId });
                }
                if (selectedThread && threadId && selectedThread.thread_id === threadId) {
                    setSelectedThread({ ...selectedThread, lead_id: leadId });
                }
            }

            toast.success(response.data?.created ? 'Lead created and attached' : 'Lead attached to thread');
        } catch (error) {
            toast.error('Failed to create lead from email');
        } finally {
            setCreatingLeadId(null);
        }
    };

    const sendEmail = async () => {
        if (!sendData.account_id || sendData.to_emails.length === 0 || !sendData.subject || !sendData.body) {
            toast.error('Please fill all required fields');
            return;
        }

        // Get lead_id if available from selected thread/email
        let leadId = null;
        if (selectedThread && selectedThread.lead_id) {
            leadId = selectedThread.lead_id;
        } else if (selectedEmail && selectedEmail.lead_id) {
            leadId = selectedEmail.lead_id;
        }

        // Validate variables before sending
        const validationResult = await validateVariables(sendData.body, sendData.subject, leadId);
        if (!validationResult.valid) {
            toast.error(`Missing required fields: ${validationResult.missingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            return;
        }

        try {
            // Send all To emails as actual To recipients (not demoting to CC)
            const payload = {
                account_id: sendData.account_id,
                to_email: sendData.to_emails[0], // Primary recipient
                subject: sendData.subject,
                body: sendData.body,
                cc_emails: sendData.cc_emails,
                bcc_emails: sendData.bcc_emails
            };

            // If multiple To recipients, add additional ones to CC
            // Note: SMTP typically supports only one To address, additional go to CC
            if (sendData.to_emails.length > 1) {
                payload.cc_emails = [...(payload.cc_emails || []), ...sendData.to_emails.slice(1)];
            }

            await axios.post(`${API_URL}/emails/send`, payload);
            toast.success('Email sent successfully');
            setSendDialogOpen(false);
            setShowCcBcc(false);
            setMissingFields([]);
            setSendData({ account_id: '', to_emails: [], cc_emails: [], bcc_emails: [], subject: '', body: '' });
            if (viewMode === 'threads') {
                fetchThreads();
            } else {
                fetchEmails();
            }
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.missing_fields) {
                const missingFields = error.response.data.missing_fields;
                setMissingFields(missingFields);
                toast.error(`Missing required fields: ${missingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            } else {
                toast.error('Failed to send email');
            }
        }
    };

    const sendReply = async () => {
        if (!replyData.account_id || replyData.to_emails.length === 0 || !replyData.body) {
            toast.error('Please fill all required fields');
            return;
        }

        // Get lead_id from thread or email context
        let leadId = null;
        if (selectedThread && selectedThread.lead_id) {
            leadId = selectedThread.lead_id;
        } else if (selectedEmail && selectedEmail.lead_id) {
            leadId = selectedEmail.lead_id;
        } else if (threadEmails.length > 0) {
            const emailWithLead = threadEmails.find(e => e.lead_id);
            if (emailWithLead) {
                leadId = emailWithLead.lead_id;
            }
        }

        // Validate variables before sending
        const validationResult = await validateVariables(replyData.body, replyData.subject, leadId);
        if (!validationResult.valid) {
            toast.error(`Missing required fields: ${validationResult.missingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            return;
        }

        try {

            // Send all To emails as actual To recipients
            const payload = {
                account_id: replyData.account_id,
                to_email: replyData.to_emails[0],
                subject: replyData.subject,
                body: replyData.body,
                cc_emails: replyData.cc_emails,
                bcc_emails: replyData.bcc_emails,
                in_reply_to: replyData.in_reply_to,
                references: replyData.references
            };

            // Include lead_id if available
            if (leadId) {
                payload.lead_id = leadId;
            }

            // If multiple To recipients, add additional ones to CC
            if (replyData.to_emails.length > 1) {
                payload.cc_emails = [...(payload.cc_emails || []), ...replyData.to_emails.slice(1)];
            }

            await axios.post(`${API_URL}/emails/send`, payload);
            toast.success('Reply sent successfully');
            setReplyDialogOpen(false);
            setShowReplyCcBcc(false);
            setMissingFields([]);
            setReplyData({
                account_id: '',
                to_emails: [],
                cc_emails: [],
                bcc_emails: [],
                subject: '',
                body: '',
                in_reply_to: null,
                references: null,
                reply_all: false
            });
            if (viewMode === 'threads') {
                fetchThreads();
                // Refresh the selected thread's emails to show the new reply
                if (selectedThread) {
                    setTimeout(() => {
                        selectThread(selectedThread);
                    }, 500); // Small delay to ensure the email is saved
                }
            } else {
                fetchEmails();
            }
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.missing_fields) {
                const missingFields = error.response.data.missing_fields;
                setMissingFields(missingFields);
                toast.error(`Missing required fields: ${missingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            } else {
                toast.error('Failed to send reply');
            }
        }
    };

    const saveComment = async (emailId, { content, mentionedUserIds }) => {
        if (!selectedThread?.thread_id || !content?.trim()) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_URL}/comments`,
                {
                    thread_id: selectedThread.thread_id,
                    email_message_id: emailId,
                    content: content.trim(),
                    mentioned_user_ids: mentionedUserIds || [],
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Comment saved');
            fetchComments(selectedThread.thread_id);
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save comment'));
        }
    };

    const getSentimentBadge = (sentiment) => {
        if (!sentiment) return null;

        const colors = {
            Positive: 'bg-green-100 text-green-700',
            Negative: 'bg-red-100 text-red-700',
            Neutral: 'bg-slate-100 text-slate-700'
        };

        return (
            <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors[sentiment]}`}>
                {sentiment}
            </span>
        );
    };

    return (
        <div className="h-[calc(100vh-4rem)]" data-testid="inbox-page">
            <div className="grid grid-cols-12 h-full">
                {/* Folders Sidebar */}
                <div className="col-span-2 border-r border-slate-200 bg-slate-50/50 p-4 hidden md:block">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-medium text-slate-900">Folders</h2>
                        <Button
                            data-testid="sync-emails-button"
                            onClick={syncEmails}
                            disabled={syncing}
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                        >
                            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>

                    {/* Mailbox Dropdown */}
                    <div className="mb-4">
                        <Label className="text-xs text-slate-600 mb-2 block">Mailbox</Label>
                        <Select value={selectedMailbox} onValueChange={setSelectedMailbox}>
                            <SelectTrigger className="rounded-lg bg-white">
                                <SelectValue placeholder="Select mailbox" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    <div className="flex items-center gap-2">
                                        <span>All Accounts</span>
                                    </div>
                                </SelectItem>
                                {accounts.map((account, index) => (
                                    <SelectItem key={account.id} value={account.id}>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${getAccountColor(account.id, accounts)}`} />
                                            <span className="truncate">{account.email}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <div
                            onClick={() => setActiveFolder('inbox')}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${activeFolder === 'inbox'
                                ? 'bg-white border border-slate-200'
                                : 'hover:bg-white/50'
                                }`}
                        >
                            <InboxIcon className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-900">Inbox</span>
                            <span className="ml-auto text-xs text-slate-500">
                                {folderCounts.inbox}
                            </span>
                        </div>
                        <div
                            onClick={() => setActiveFolder('sent')}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${activeFolder === 'sent'
                                ? 'bg-white border border-slate-200'
                                : 'hover:bg-white/50'
                                }`}
                        >
                            <SendHorizontal className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-900">Sent</span>
                            <span className="ml-auto text-xs text-slate-500">
                                {folderCounts.sent}
                            </span>
                        </div>
                        <div
                            onClick={() => setActiveFolder('discard')}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${activeFolder === 'discard'
                                ? 'bg-white border border-slate-200'
                                : 'hover:bg-white/50'
                                }`}
                        >
                            <Trash2 className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-900">Discard</span>
                            <span className="ml-auto text-xs text-slate-500">
                                {folderCounts.discard}
                            </span>
                        </div>
                        <div
                            onClick={() => setActiveFolder('junk')}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${activeFolder === 'junk'
                                ? 'bg-white border border-slate-200'
                                : 'hover:bg-white/50'
                                }`}
                        >
                            <Archive className="h-4 w-4 text-slate-600" />
                            <span className="text-sm text-slate-900">Junk / Ignored</span>
                            <span className="ml-auto text-xs text-slate-500">
                                {folderCounts.junk || 0}
                            </span>
                        </div>
                    </div>

                    <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                data-testid="compose-email-button"
                                className="w-full mt-6 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                <Mail className="h-4 w-4 mr-2" />
                                Compose
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-2xl font-semibold text-slate-900">New Email</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                                <div>
                                    <Label>From Account</Label>
                                    <Select value={sendData.account_id} onValueChange={handleAccountSelect}>
                                        <SelectTrigger data-testid="select-account" className="rounded-lg">
                                            <SelectValue placeholder="Select account" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {accounts.map((acc) => (
                                                <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>To</Label>
                                    <EmailChipInput
                                        emails={sendData.to_emails}
                                        onChange={(emails) => setSendData({ ...sendData, to_emails: emails })}
                                        placeholder="recipient@example.com"
                                    />
                                </div>
                                {!showCcBcc && (
                                    <button
                                        onClick={() => setShowCcBcc(true)}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        + Show Cc/Bcc
                                    </button>
                                )}
                                {showCcBcc && (
                                    <>
                                        <div>
                                            <Label>Cc</Label>
                                            <EmailChipInput
                                                emails={sendData.cc_emails}
                                                onChange={(emails) => setSendData({ ...sendData, cc_emails: emails })}
                                                placeholder="cc@example.com"
                                            />
                                        </div>
                                        <div>
                                            <Label>Bcc</Label>
                                            <EmailChipInput
                                                emails={sendData.bcc_emails}
                                                onChange={(emails) => setSendData({ ...sendData, bcc_emails: emails })}
                                                placeholder="bcc@example.com"
                                            />
                                        </div>
                                    </>
                                )}
                                <div>
                                    <Label>Subject</Label>
                                    <Input
                                        data-testid="email-subject-input"
                                        value={sendData.subject}
                                        onChange={(e) => {
                                            setSendData({ ...sendData, subject: e.target.value });
                                            if (missingFields.length > 0) {
                                                validateVariables(sendData.body, e.target.value, selectedThread?.lead_id || selectedEmail?.lead_id);
                                            }
                                        }}
                                        placeholder="Email subject"
                                        className={`rounded-lg ${missingFields.length > 0 ? 'border-red-300 focus:ring-red-500' : ''
                                            }`}
                                    />
                                </div>
                                {missingFields.length > 0 && (
                                    <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-red-900">Missing Required Fields</p>
                                                <p className="text-xs text-red-700 mt-1">
                                                    The following variables are missing data: {missingFields.map(f => `{{${f}}}`).join(', ')}
                                                </p>
                                                <p className="text-xs text-red-600 mt-1">
                                                    Add fallbacks like {`{{${missingFields[0]} | there}}`} or fill the missing lead data.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Label>Message</Label>
                                    <RichTextEditor
                                        content={sendData.body}
                                        accountId={sendData.account_id}
                                        leadId={selectedThread?.lead_id || selectedEmail?.lead_id}
                                        onChange={(html) => {
                                            setSendData({ ...sendData, body: html });
                                            if (missingFields.length > 0) {
                                                validateVariables(html, sendData.subject, selectedThread?.lead_id || selectedEmail?.lead_id);
                                            }
                                        }}
                                        placeholder="Write your email... Type / to insert a template"
                                        onMissingFields={setMissingFields}
                                        onSubjectChange={(subject) => setSendData({ ...sendData, subject })}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        data-testid="send-email-button"
                                        onClick={sendEmail}
                                        disabled={missingFields.length > 0}
                                        className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send className="h-4 w-4 mr-2" />
                                        Send Email
                                    </Button>
                                    <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="rounded-lg font-medium transition-all active:scale-95"
                                                onClick={() => setSaveTemplateDialogOpen(true)}
                                            >
                                                <Save className="h-4 w-4 mr-2" />
                                                Save as Template
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-md rounded-2xl">
                                            <DialogHeader>
                                                <DialogTitle className="text-2xl font-semibold text-slate-900">Save as Template</DialogTitle>
                                            </DialogHeader>
                                            <div className="space-y-4 mt-4">
                                                <div>
                                                    <Label>Template Name</Label>
                                                    <Input
                                                        value={templateName}
                                                        onChange={(e) => setTemplateName(e.target.value)}
                                                        placeholder="e.g., Follow-up V2"
                                                        className="rounded-lg"
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={async () => {
                                                            if (!templateName.trim()) {
                                                                toast.error('Please enter a template name');
                                                                return;
                                                            }
                                                            try {
                                                                const token = localStorage.getItem('token');
                                                                await axios.post(
                                                                    `${API_URL}/templates`,
                                                                    {
                                                                        name: templateName,
                                                                        subject: sendData.subject,
                                                                        body_html: sendData.body,
                                                                        is_global: true
                                                                    },
                                                                    {
                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                    }
                                                                );
                                                                toast.success('Template saved successfully');
                                                                setSaveTemplateDialogOpen(false);
                                                                setTemplateName('');
                                                            } catch (error) {
                                                                toast.error(getApiError(error, 'Failed to save template'));
                                                            }
                                                        }}
                                                        className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                                    >
                                                        <Save className="h-4 w-4 mr-2" />
                                                        Save Template
                                                    </Button>
                                                    <Button
                                                        onClick={() => setSaveTemplateDialogOpen(false)}
                                                        variant="outline"
                                                        className="rounded-lg font-medium transition-all active:scale-95"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Reply Dialog */}
                    <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
                        <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="text-2xl font-semibold text-slate-900">
                                    {replyData.reply_all ? 'Reply All' : 'Reply'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                                <div>
                                    <Label>From Account</Label>
                                    <Select value={replyData.account_id} onValueChange={(value) => handleAccountSelect(value, true)}>
                                        <SelectTrigger className="rounded-lg">
                                            <SelectValue placeholder="Select account" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {accounts.map((acc) => (
                                                <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>To</Label>
                                    <EmailChipInput
                                        emails={replyData.to_emails}
                                        onChange={(emails) => setReplyData({ ...replyData, to_emails: emails })}
                                        placeholder="recipient@example.com"
                                    />
                                </div>
                                {!showReplyCcBcc && replyData.cc_emails.length === 0 && (
                                    <button
                                        onClick={() => setShowReplyCcBcc(true)}
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        + Show Cc/Bcc
                                    </button>
                                )}
                                {(showReplyCcBcc || replyData.cc_emails.length > 0) && (
                                    <>
                                        <div>
                                            <Label>Cc</Label>
                                            <EmailChipInput
                                                emails={replyData.cc_emails}
                                                onChange={(emails) => setReplyData({ ...replyData, cc_emails: emails })}
                                                placeholder="cc@example.com"
                                            />
                                        </div>
                                        <div>
                                            <Label>Bcc</Label>
                                            <EmailChipInput
                                                emails={replyData.bcc_emails}
                                                onChange={(emails) => setReplyData({ ...replyData, bcc_emails: emails })}
                                                placeholder="bcc@example.com"
                                            />
                                        </div>
                                    </>
                                )}
                                <div>
                                    <Label>Subject</Label>
                                    <Input
                                        value={replyData.subject}
                                        onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                                        placeholder="Email subject"
                                        className="rounded-lg"
                                    />
                                </div>
                                <div>
                                    <Label>Message</Label>
                                    <RichTextEditor
                                        content={replyData.body}
                                        onChange={(html) => setReplyData({ ...replyData, body: html })}
                                        placeholder="Write your reply..."
                                    />
                                </div>
                                <Button
                                    onClick={sendReply}
                                    className="w-full rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    Send Reply
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Email/Thread List */}
                <div className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-slate-200 bg-white overflow-y-auto">
                    <div className="p-4 border-b border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-xl font-medium text-slate-900">
                                {activeFolder === 'inbox' ? 'Inbox' : activeFolder === 'sent' ? 'Sent' : activeFolder === 'junk' ? 'Junk / Ignored' : 'Discard'}
                            </h2>
                            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('threads')}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${viewMode === 'threads' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                >
                                    Threads
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('emails')}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${viewMode === 'emails' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                                >
                                    Emails
                                </button>
                            </div>
                        </div>
                        {activeFolder === 'inbox' && viewMode === 'threads' && (
                            <div className="flex gap-1 mb-2">
                                <button
                                    type="button"
                                    onClick={() => setFilterMode('all')}
                                    className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${filterMode === 'all' ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFilterMode('mentioned')}
                                    className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${filterMode === 'mentioned' ? 'bg-amber-100 text-amber-800' : 'text-slate-600 hover:bg-slate-100'}`}
                                >
                                    <AtSign className="h-3 w-3" /> @ Me
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            {activeFolder !== 'discard' && (
                                <Checkbox
                                    checked={
                                        viewMode === 'threads'
                                            ? threads.length > 0 && selectedThreadIds.size === threads.length
                                            : emails.length > 0 && selectedEmailIds.size === emails.length
                                    }
                                    onCheckedChange={() => viewMode === 'threads' ? selectAllThreads() : selectAllEmails()}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex-shrink-0"
                                />
                            )}
                            <p className="text-sm text-slate-600">
                                {viewMode === 'threads'
                                    ? `${totalThreads} conversation${totalThreads !== 1 ? 's' : ''}`
                                    : `${totalEmails} email${totalEmails !== 1 ? 's' : ''}`}
                            </p>
                        </div>
                        {(selectedThreadIds.size > 0 || selectedEmailIds.size > 0) && activeFolder !== 'discard' && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-slate-600">
                                    {viewMode === 'threads' ? selectedThreadIds.size : selectedEmailIds.size} selected
                                </span>
                                <Button size="sm" variant="destructive" onClick={bulkMoveToDiscard} className="h-7 text-xs">
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Move to Discard
                                </Button>
                                <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 text-xs">
                                    Clear
                                </Button>
                            </div>
                        )}
                    </div>
                    <ScrollArea className="h-[calc(100vh-10rem)]">
                        {viewMode === 'threads' ? (
                            <>
                                {threads.map((thread) => (
                                    <div
                                        key={thread.thread_id}
                                        data-testid={`thread-item-${thread.thread_id}`}
                                        onClick={() => selectThread(thread)}
                                        className={`p-3 sm:p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors group relative ${selectedThread?.thread_id === thread.thread_id ? 'bg-blue-50/50 border-l-4 border-l-blue-600' : ''
                                            } ${!thread.is_read ? 'bg-white font-semibold' : 'bg-gray-50/30'}`}
                                        style={{ minWidth: 0, overflow: 'hidden' }}
                                    >
                                        <div className="flex items-start gap-2 mb-1">
                                            {activeFolder !== 'discard' && (
                                                <Checkbox
                                                    checked={selectedThreadIds.has(thread.thread_id)}
                                                    onCheckedChange={() => toggleThreadSelection(thread.thread_id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex-shrink-0 mt-0.5"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between mb-1 gap-1.5 min-w-0">
                                                    <div className="flex-1 min-w-0 overflow-hidden">
                                                        <p className={`text-slate-900 text-sm truncate ${!thread.is_read ? 'font-bold' : 'font-semibold'}`}>
                                                            {thread.participants.slice(0, 2).join(', ')}
                                                            {thread.participants.length > 2 && ` +${thread.participants.length - 2}`}
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col items-end flex-shrink-0 whitespace-nowrap" style={{ minWidth: 'fit-content' }}>
                                                        <span className="text-xs text-slate-500">
                                                            {format(new Date(thread.last_message_at), 'MMM d')}
                                                        </span>
                                                        <span className="text-xs text-slate-400">
                                                            {format(new Date(thread.last_message_at), 'h:mm a')}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 mb-1 min-w-0">
                                                    {selectedMailbox === 'all' && (
                                                        <div
                                                            className={`w-2 h-2 rounded-full flex-shrink-0 ${getAccountColor(thread.account_id, accounts)}`}
                                                            title={accountMap.get(thread.account_id)?.email || 'Unknown account'}
                                                        />
                                                    )}
                                                    <p className={`text-sm text-slate-900 truncate flex-1 min-w-0 ${!thread.is_read ? 'font-semibold' : 'font-medium'}`} style={{ minWidth: 0 }}>
                                                        {thread.subject}
                                                    </p>
                                                    {thread.message_count > 1 && (
                                                        <span className="bg-slate-200 text-slate-700 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ flexShrink: 0 }}>
                                                            {thread.message_count}
                                                        </span>
                                                    )}
                                                    {thread.lead_id && (
                                                        <button
                                                            type="button"
                                                            className="h-6 w-6 p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded flex items-center justify-center transition-colors border border-blue-200 bg-blue-50 opacity-0 group-hover:opacity-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/leads/${thread.lead_id}`);
                                                            }}
                                                            title="Go to Lead"
                                                            style={{ minWidth: '24px', minHeight: '24px', maxWidth: '24px', maxHeight: '24px', flexShrink: 0, display: 'flex' }}
                                                        >
                                                            <ExternalLink className="h-3 w-3" style={{ display: 'block', flexShrink: 0 }} />
                                                        </button>
                                                    )}
                                                    {activeFolder !== 'discard' && (
                                                        <button
                                                            type="button"
                                                            className="h-6 w-6 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded flex items-center justify-center transition-colors border border-red-200 bg-white opacity-0 group-hover:opacity-100"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    const token = localStorage.getItem('token');
                                                                    const { data } = await axios.post(
                                                                        `${API_URL}/emails/move-thread-to-discard`,
                                                                        { thread_id: thread.thread_id },
                                                                        { headers: { Authorization: `Bearer ${token}` } }
                                                                    );
                                                                    toast.success(data.message || 'Thread moved to discard');
                                                                    fetchThreads();
                                                                    fetchFolderCounts();
                                                                    if (selectedThread?.thread_id === thread.thread_id) {
                                                                        setSelectedThread(null);
                                                                        setThreadEmails([]);
                                                                    }
                                                                } catch (err) {
                                                                    toast.error(getApiError(err, 'Failed to move thread'));
                                                                }
                                                            }}
                                                            title="Move Thread to Discard"
                                                            style={{ minWidth: '24px', minHeight: '24px', maxWidth: '24px', maxHeight: '24px', flexShrink: 0, display: 'flex' }}
                                                        >
                                                            <Trash2 className="h-3 w-3" style={{ display: 'block', flexShrink: 0 }} />
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-600 line-clamp-2 break-words overflow-hidden">{thread.snippet}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <LoadMoreSentinel
                                    onLoadMore={fetchMoreThreads}
                                    hasMore={hasMoreThreads}
                                    loading={loadingMoreThreads}
                                />
                            </>
                        ) : (
                            <>
                                {emails.map((email) => (
                                    <div
                                        key={email.id}
                                        data-testid={`email-item-${email.id}`}
                                        onClick={() => setSelectedEmail(email)}
                                        className={`p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors group ${selectedEmail?.id === email.id ? 'bg-blue-50/50 border-l-4 border-l-blue-600' : ''
                                            }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            {activeFolder !== 'discard' && (
                                                <Checkbox
                                                    checked={selectedEmailIds.has(email.id)}
                                                    onCheckedChange={() => toggleEmailSelection(email.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex-shrink-0 mt-0.5"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between mb-1 gap-2 min-w-0">
                                                    <p className="font-semibold text-slate-900 text-sm truncate flex-1 min-w-0">
                                                        {email.direction === 'inbound' ? email.from_email : email.to_email}
                                                    </p>
                                                    <span className="text-xs text-slate-500 flex-shrink-0">
                                                        {format(new Date(email.created_at), 'MMM d')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                                    {/* Show colored dot when in unified view (all accounts) */}
                                                    {selectedMailbox === 'all' && (
                                                        <div
                                                            className={`w-2 h-2 rounded-full flex-shrink-0 ${getAccountColor(email.account_id, accounts)}`}
                                                            title={accountMap.get(email.account_id)?.email || 'Unknown account'}
                                                        />
                                                    )}
                                                    <p className="text-sm text-slate-900 font-medium truncate flex-1 min-w-0">{email.subject}</p>
                                                </div>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <p className="text-xs text-slate-600 truncate flex-1 min-w-0">{(email.body || '').substring(0, 60)}{(email.body || '').length > 60 ? '...' : ''}</p>
                                                    {/* Story 14.4: Eye icon for email open tracking */}
                                                    {email.direction === 'outbound' && (
                                                        <div className="flex-shrink-0 flex items-center gap-1" title={email.is_opened ? `Opened ${email.open_count || 0} ${email.open_count === 1 ? 'time' : 'times'}${email.last_opened_at ? `, last on ${format(new Date(email.last_opened_at), 'MMM d')}` : ''}` : 'Not opened'}>
                                                            <Eye className={`h-4 w-4 ${email.is_opened ? 'text-green-600' : 'text-slate-400'}`} />
                                                            {email.is_opened && email.open_count > 0 && (
                                                                <span className="text-xs text-slate-500">{email.open_count}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {email.sentiment && (
                                                        <div className="flex-shrink-0">
                                                            {getSentimentBadge(email.sentiment)}
                                                        </div>
                                                    )}
                                                    {email.lead_id && email.direction === 'inbound' && (
                                                        <button
                                                            type="button"
                                                            className="h-7 w-7 p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex-shrink-0 rounded flex items-center justify-center transition-colors border border-blue-200 bg-blue-50 opacity-0 group-hover:opacity-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/leads/${email.lead_id}`);
                                                            }}
                                                            title="Go to Lead"
                                                            style={{ minWidth: '28px', minHeight: '28px', display: 'flex' }}
                                                        >
                                                            <ExternalLink className="h-4 w-4" style={{ display: 'block' }} />
                                                        </button>
                                                    )}
                                                    {!email.lead_id && email.direction === 'inbound' && activeFolder === 'inbox' && (
                                                        <button
                                                            type="button"
                                                            className="h-7 w-7 p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex-shrink-0 rounded flex items-center justify-center transition-colors border border-blue-200 bg-white opacity-0 group-hover:opacity-100 disabled:opacity-60"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                createLeadFromEmail(email);
                                                            }}
                                                            title="Create Lead"
                                                            style={{ minWidth: '28px', minHeight: '28px', display: 'flex' }}
                                                            disabled={creatingLeadId === email.id}
                                                        >
                                                            <UserPlus className="h-4 w-4" style={{ display: 'block' }} />
                                                        </button>
                                                    )}
                                                    {activeFolder !== 'discard' && (
                                                        <button
                                                            type="button"
                                                            className="h-7 w-7 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 rounded flex items-center justify-center transition-colors border border-red-200 bg-white"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                console.log('Trash clicked for email:', email.id, 'activeFolder:', activeFolder);
                                                                moveToDiscard(email.id);
                                                            }}
                                                            title="Move to Discard"
                                                            style={{ minWidth: '28px', minHeight: '28px', display: 'flex' }}
                                                        >
                                                            <Trash2 className="h-4 w-4" style={{ display: 'block' }} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <LoadMoreSentinel
                                    onLoadMore={fetchMoreEmails}
                                    hasMore={hasMoreEmails}
                                    loading={loadingMoreEmails}
                                />
                            </>
                        )}
                    </ScrollArea>
                </div>

                {/* Email/Thread Content */}
                <div className="col-span-12 md:col-span-6 lg:col-span-7 bg-white p-8 overflow-y-auto">
                    {selectedThread && threadEmails.length > 0 ? (
                        // Thread conversation view
                        <div data-testid="thread-content">
                            <div className="mb-6 pb-6 border-b border-slate-200">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <h2 className="text-2xl font-semibold text-slate-900 mb-2">{selectedThread.subject}</h2>
                                        <p className="text-sm text-slate-600">{threadEmails.length} messages</p>
                                    </div>
                                    <div className="flex flex-col gap-2 ml-4">
                                        {selectedThread.lead_id && (
                                            <Button
                                                onClick={() => navigate(`/leads/${selectedThread.lead_id}`)}
                                                size="sm"
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                Go to Lead
                                            </Button>
                                        )}
                                        {!selectedThread.lead_id && activeFolder === 'inbox' && inboundThreadEmail && (
                                            <Button
                                                onClick={() => createLeadFromEmail(inboundThreadEmail)}
                                                size="sm"
                                                variant="outline"
                                                disabled={creatingLeadId === inboundThreadEmail.id}
                                                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                                            >
                                                <UserPlus className="h-4 w-4 mr-2" />
                                                Create Lead
                                            </Button>
                                        )}
                                        {activeFolder !== 'discard' && threadEmails.length > 0 && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                onClick={async () => {
                                                    // Move all emails in thread to discard
                                                    for (const email of threadEmails) {
                                                        await moveToDiscard(email.id);
                                                    }
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Move Thread to Discard
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <ScrollArea className="h-[calc(100vh-16rem)]">
                                <div className="space-y-4">
                                    {threadEmails.map((email) => {
                                        const emailComments = comments.filter((c) => c.email_message_id === email.id);
                                        return (
                                            <div
                                                key={email.id}
                                                className={`flex ${email.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`max-w-[80%] rounded-lg p-4 ${email.direction === 'outbound'
                                                        ? 'bg-amber-50 border border-amber-200 text-slate-800'
                                                        : 'bg-white border border-slate-200 text-slate-900'
                                                        }`}
                                                >
                                                    {/* Epic 18.3: Header Details - From, To, CC/BCC */}
                                                    <div className="mb-2 space-y-1 text-xs">
                                                        <p className="font-medium text-slate-600">
                                                            From: {email.from_email}
                                                        </p>
                                                        <p className="text-slate-500">
                                                            To: {Array.isArray(email.to_email) ? email.to_email.join(', ') : email.to_email || '-'}
                                                        </p>
                                                        {(email.cc_emails?.length > 0 || email.bcc_emails?.length > 0) && (
                                                            <details className="text-slate-500">
                                                                <summary className="cursor-pointer hover:text-slate-700">CC/BCC</summary>
                                                                {email.cc_emails?.length > 0 && (
                                                                    <p className="mt-1">CC: {email.cc_emails.join(', ')}</p>
                                                                )}
                                                                {email.bcc_emails?.length > 0 && (
                                                                    <p className="mt-1">BCC: {email.bcc_emails.join(', ')}</p>
                                                                )}
                                                            </details>
                                                        )}
                                                    </div>
                                                    <div className="flex items-start justify-between mb-2">
                                                        <p className="text-xs text-slate-500">
                                                            {format(new Date(email.created_at), 'PPp')}
                                                        </p>
                                                        {activeFolder !== 'discard' && (
                                                            <button
                                                                type="button"
                                                                className={`h-6 w-6 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 rounded flex items-center justify-center transition-colors border border-red-200 ${email.direction === 'outbound' ? 'bg-red-50 border-red-300' : 'bg-white'}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveToDiscard(email.id);
                                                                }}
                                                                title="Move to Discard"
                                                                style={{ minWidth: '24px', minHeight: '24px', display: 'flex' }}
                                                            >
                                                                <Trash2 className="h-3 w-3" style={{ display: 'block' }} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {/* Epic 18.3: HTML Sandbox for email body - collapsible quoted in thread view */}
                                                    <EmailBodySandbox
                                                        content={email.body}
                                                        className="text-sm"
                                                        isDark={false}
                                                        collapsibleQuoted={true}
                                                    />
                                                    {/* Epic 18.1: Internal Note box - comments below email body */}
                                                    {emailComments.length > 0 && (
                                                        <div className="mt-3 space-y-2">
                                                            {emailComments.map((c) => (
                                                                <div
                                                                    key={c.id}
                                                                    className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-slate-800"
                                                                >
                                                                    <p className="font-medium text-amber-800 text-xs mb-1">
                                                                        Internal Note · {c.user_email} · {format(new Date(c.created_at), 'PPp')}
                                                                    </p>
                                                                    <p className="whitespace-pre-wrap">{c.content}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {/* Epic 18.1: Comment button - expand to show input */}
                                                    {activeFolder !== 'discard' && (
                                                        <CommentButtonWithInput
                                                            teamMembers={teamMembers}
                                                            onSave={(data) => saveComment(email.id, data)}
                                                        />
                                                    )}
                                                    {email.direction === 'inbound' && activeFolder === 'inbox' && (
                                                        <div className="flex gap-2 mt-3">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-7 text-xs"
                                                                onClick={() => {
                                                                    const replyEmail = threadEmails.find(e => e.direction === 'inbound') || email;
                                                                    startReply(replyEmail, false);
                                                                }}
                                                            >
                                                                <Reply className="h-3 w-3 mr-1" />
                                                                Reply
                                                            </Button>
                                                            {email.lead_id && (
                                                                <Button
                                                                    size="sm"
                                                                    className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                                                    onClick={() => navigate(`/leads/${email.lead_id}`)}
                                                                >
                                                                    <ExternalLink className="h-3 w-3 mr-1" />
                                                                    Go to Lead
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        </div>
                    ) : selectedEmail ? (
                        // Single email view
                        <div data-testid="email-content">
                            <div className="mb-6 pb-6 border-b border-slate-200">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h2 className="text-2xl font-semibold text-slate-900 mb-2">{selectedEmail.subject}</h2>
                                        <p className="text-sm text-slate-600">
                                            <span className="font-medium">From:</span> {selectedEmail.from_email}
                                        </p>
                                        <p className="text-sm text-slate-600">
                                            <span className="font-medium">To:</span> {selectedEmail.to_email}
                                        </p>
                                        {selectedEmail.cc_emails && selectedEmail.cc_emails.length > 0 && (
                                            <p className="text-sm text-slate-600">
                                                <span className="font-medium">Cc:</span> {selectedEmail.cc_emails.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-slate-600">
                                            {format(new Date(selectedEmail.created_at), 'PPpp')}
                                        </p>
                                        {/* Story 14.4: Eye icon for email open tracking - show in detail view */}
                                        {selectedEmail.direction === 'outbound' && (
                                            <div className="mt-2 flex items-center justify-end gap-2">
                                                <div className="flex items-center gap-1.5" title={selectedEmail.is_opened ? `Opened ${selectedEmail.open_count || 0} ${selectedEmail.open_count === 1 ? 'time' : 'times'}${selectedEmail.last_opened_at ? `, last on ${format(new Date(selectedEmail.last_opened_at), 'MMM d, h:mm a')}` : ''}` : 'Not opened'}>
                                                    <Eye className={`h-4 w-4 ${selectedEmail.is_opened ? 'text-green-600' : 'text-slate-400'}`} />
                                                    {selectedEmail.is_opened && (selectedEmail.open_count ?? 0) > 0 && (
                                                        <span className="text-xs text-slate-600">
                                                            {selectedEmail.open_count}x
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {selectedEmail.sentiment && (
                                            <div className="mt-2">{getSentimentBadge(selectedEmail.sentiment)}</div>
                                        )}
                                        {/* Always show trash icon for debugging */}
                                        <button
                                            type="button"
                                            className="mt-3 h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded border border-red-200 flex items-center justify-center transition-colors bg-white"
                                            onClick={() => {
                                                console.log('Trash clicked in header for email:', selectedEmail.id, 'activeFolder:', activeFolder, 'direction:', selectedEmail.direction);
                                                if (activeFolder !== 'discard') {
                                                    moveToDiscard(selectedEmail.id);
                                                }
                                            }}
                                            title="Move to Discard"
                                            style={{
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                display: 'flex',
                                                visibility: activeFolder === 'discard' ? 'hidden' : 'visible'
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" style={{ display: 'block' }} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="max-w-none mb-6">
                                <EmailBodySandbox
                                    content={selectedEmail.body}
                                    className="text-sm leading-relaxed"
                                    isDark={false}
                                    collapsibleQuoted={true}
                                />
                            </div>
                            <div className="flex gap-2 pt-4 border-t border-slate-200 justify-between items-center flex-wrap">
                                {selectedEmail.direction === 'inbound' && activeFolder === 'inbox' && (
                                    <div className="flex gap-2 flex-wrap">
                                        <Button
                                            onClick={() => startReply(selectedEmail, false)}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <Reply className="h-4 w-4 mr-2" />
                                            Reply
                                        </Button>
                                        <Button
                                            onClick={() => startReply(selectedEmail, true)}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <Reply className="h-4 w-4 mr-2" />
                                            Reply All
                                        </Button>
                                        {selectedEmail.lead_id && (
                                            <Button
                                                onClick={() => navigate(`/leads/${selectedEmail.lead_id}`)}
                                                size="sm"
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                Go to Lead
                                            </Button>
                                        )}
                                        {!selectedEmail.lead_id && (
                                            <Button
                                                onClick={() => createLeadFromEmail(selectedEmail)}
                                                size="sm"
                                                variant="outline"
                                                disabled={creatingLeadId === selectedEmail.id}
                                                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                                            >
                                                <UserPlus className="h-4 w-4 mr-2" />
                                                Create Lead
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {activeFolder !== 'discard' && (
                                    <button
                                        type="button"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-md px-3 py-1.5 text-sm font-medium flex items-center gap-2 flex-shrink-0 transition-colors bg-white"
                                        onClick={() => {
                                            console.log('Trash clicked in footer for email:', selectedEmail.id, 'activeFolder:', activeFolder);
                                            moveToDiscard(selectedEmail.id);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        <span className="hidden sm:inline">Move to Discard</span>
                                        <span className="sm:hidden">Discard</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-slate-500">
                            <div className="text-center">
                                <Mail className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                                <p className="text-lg">Select an email to view</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InboxPage;
